import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container, Box, Typography, Button, Grid, Paper, CircularProgress,
  useTheme, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Link, Divider
} from '@mui/material';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
} from 'chart.js';
import { Pie, Bar } from 'react-chartjs-2';
import { motion } from 'framer-motion';
import AddIcon from '@mui/icons-material/Add';
import InventoryIcon from '@mui/icons-material/Inventory';
import ReceiptIcon from '@mui/icons-material/Receipt';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import WarningIcon from '@mui/icons-material/Warning';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import StarIcon from '@mui/icons-material/Star';
import BuildIcon from '@mui/icons-material/Build';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import StorefrontIcon from '@mui/icons-material/Storefront';
import HandshakeIcon from '@mui/icons-material/Handshake';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import AssignmentLateIcon from '@mui/icons-material/AssignmentLate';
import useIsMobile from '../hooks/useIsMobile';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  ChartDataLabels
);

function Home() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useIsMobile();
  const [dashboardData, setDashboardData] = useState({
    total_vehicles: 0,
    total_inventory_value: 0,
    current_month_profit: 0,
    current_month_reconditioning_cost: 0,
    total_reconditioning_cost: 0,
    total_floor_plan: 0,
    total_dealership: 0,
    total_consignment: 0,
    unsold_reconditioning_cost: 0,
    total_expenses: 0,
    current_month_deposits: 0,
    incomplete_tasks: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const username = localStorage.getItem('username');
        console.log('Stored username:', username);
        
        if (!username) {
          console.error('No username in localStorage');
          return;
        }

        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/dashboard?username=${username}`,
          {
            method: 'GET',
            credentials: 'include',
            headers: {
              'Accept': 'application/json',
              'Content-Type': 'application/json'
            }
          }
        );

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to fetch dashboard data');
        }

        const data = await response.json();
        console.log('Raw Dashboard data:', data);
        console.log('Reconditioning data:', {
          cost: data.current_month_reconditioning_cost,
          month: data.current_month_name,
          reports: data.reconditioning_reports?.length || 0
        });

        setDashboardData({
          ...data,
          current_month_reconditioning_cost: parseFloat(data.current_month_reconditioning_cost || 0),
          current_month_name: data.current_month_name,
          reconditioning_reports: data.reconditioning_reports || [],
          incomplete_tasks: data.incomplete_tasks || 0,
          total_expenses: data.total_expenses || 0
        });

      } catch (err) {
        console.error('Dashboard Error:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  const handleViewInventory = () => navigate('/inventory');
  const handleAddCar = () => navigate('/add-car');
  const handleAddExpenseReport = () => navigate('/add-expense-report');

  const getReconditioningData = (reports) => {
    // Get last 6 months
    const today = new Date();
    const months = [];
    for (let i = 0; i < 6; i++) {
      const month = new Date(today.getFullYear(), today.getMonth() - i, 1);
      months.push({
        month: month.toLocaleString('default', { month: 'long' }),
        year: month.getFullYear(),
        monthNum: month.getMonth()
      });
    }

    // Calculate reconditioning costs for each month
    const monthlyReconditioning = months.map(monthData => {
      const monthCost = reports
        .filter(report => {
          const reportDate = new Date(report.date_occurred);
          return reportDate.getMonth() === monthData.monthNum && 
                 reportDate.getFullYear() === monthData.year;
        })
        .reduce((total, report) => total + (parseFloat(report.cost) || 0), 0);

      return {
        label: `${monthData.month} ${monthData.year}`,
        cost: Math.round(monthCost)
      };
    });

    return {
      labels: monthlyReconditioning.map(m => m.label).reverse(),  // Show oldest to newest
      datasets: [{
        label: 'Reconditioning Cost',
        data: monthlyReconditioning.map(m => m.cost).reverse(),
        backgroundColor: monthlyReconditioning.map(() => 'rgba(54, 162, 235, 0.75)'),
        borderColor: monthlyReconditioning.map(() => 'rgba(54, 162, 235, 1)'),
        borderWidth: 1,
        borderRadius: 6,
        maxBarThickness: 50,
        minBarLength: 6
      }]
    };
  };

  const pieData = {
    labels: ['Floor Plan', 'Dealership', 'Consignment'],
    datasets: [{
      data: [
        dashboardData?.total_floor_plan || 0,
        dashboardData?.total_dealership || 0,
        dashboardData?.total_consignment || 0
      ],
      backgroundColor: [
        'rgba(45, 136, 255, 0.85)',    // Vibrant Blue
        'rgba(255, 92, 147, 0.85)',    // Vibrant Pink
        'rgba(255, 187, 51, 0.85)'     // Vibrant Gold
      ],
      hoverBackgroundColor: [
        'rgba(45, 136, 255, 1)',
        'rgba(255, 92, 147, 1)',
        'rgba(255, 187, 51, 1)'
      ],
      borderWidth: 3,
      borderColor: '#ffffff'
    }]
  };

  const pieOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      tooltip: {
        enabled: true,
        backgroundColor: 'rgba(17, 24, 39, 0.95)',
        padding: {
          top: 8,
          right: 12,
          bottom: 8,
          left: 12
        },
        titleFont: {
          size: 12,
          weight: 'bold',
          family: "'Inter', sans-serif"
        },
        bodyFont: {
          size: 11,
          family: "'Inter', sans-serif"
        },
        callbacks: {
          label: function(tooltipItem) {
            const total = tooltipItem.dataset.data.reduce((sum, value) => sum + value, 0);
            const value = tooltipItem.raw;
            const percent = ((value / total) * 100).toFixed(1);
            return ` ${value} (${percent}%)`;
          }
        }
      },
      legend: {
        display: true,
        position: 'bottom',
        labels: {
          padding: 15,
          font: {
            size: 11
          }
        }
      }
    },
    cutout: '70%'
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: "spring",
        stiffness: 100
      }
    }
  };

  const buttonHoverVariants = {
    hover: {
      scale: 1.05,
      boxShadow: "0px 5px 15px rgba(0,0,0,0.2)",
      transition: {
        type: "spring",
        stiffness: 400
      }
    }
  };

  const calculateAverageDaysToSale = (inventory) => {
    if (!inventory || inventory.length === 0) return '0 Days';
    const soldCars = inventory.filter(car => car.sale_status === 'sold' && car.purchase_date && car.date_sold);
    if (!soldCars.length) return '0 Days';
    const totalDays = soldCars.reduce((sum, car) => {
      const purchaseDate = new Date(car.purchase_date);
      const saleDate = new Date(car.date_sold);
      const diffTime = Math.abs(saleDate - purchaseDate);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return sum + diffDays;
    }, 0);
    const averageDays = Math.round(totalDays / soldCars.length);
    return `${averageDays} Days`;
  };

  const calculateAverageAgeOfCurrentInventory = (inventory) => {
    if (!inventory || inventory.length === 0) return '0 Days';
    const unsoldCars = inventory.filter(car => car.sale_status !== 'sold' && car.purchase_date);
    if (!unsoldCars.length) return '0 Days';
    const today = new Date();
    const totalDays = unsoldCars.reduce((sum, car) => {
      const purchaseDate = new Date(car.purchase_date);
      const diffTime = Math.abs(today - purchaseDate);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return sum + diffDays;
    }, 0);
    const averageDays = Math.round(totalDays / unsoldCars.length);
    return `${averageDays} Days`;
  };

  const countPendingTitles = (inventory) => {
    return inventory.filter(car => 
      car.title_received === 'no' && car.sale_status !== 'sold'
    ).length;
  };

  const calculateAverageMargin = (inventory) => {
    const soldCars = inventory.filter(car => car.sale_status === 'sold');
    if (!soldCars.length) return '0';
    
    const totalMargin = soldCars.reduce((sum, car) => {
      const profit = car.sale_price - car.purchase_price;
      return sum + (profit / car.purchase_price) * 100;
    }, 0);
    
    return Math.round(totalMargin / soldCars.length);
  };

  const calculateInventoryTurnover = (inventory) => {
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    
    const recentSales = inventory.filter(car => 
      car.sale_status === 'sold' && 
      new Date(car.date_sold) > thirtyDaysAgo
    ).length;
    
    return `${recentSales}/mo`;
  };

  const getTopMakes = (inventory) => {
    const makes = inventory.reduce((acc, car) => {
      acc[car.make] = (acc[car.make] || 0) + 1;
      return acc;
    }, {});
    
    return Object.entries(makes)
      .sort(([,a], [,b]) => b - a)
      .slice(0, 3)
      .map(([make]) => make)
      .join(', ');
  };

  const countPendingInspections = (inventory) => {
    return inventory.filter(car => 
      car.inspection_received === 'no' && car.sale_status !== 'sold'
    ).length;
  };

  const getPriceDistributionData = (inventory) => {
    const priceRanges = [
      { min: 0, max: 10000, label: '0-10k' },
      { min: 10000, max: 20000, label: '10k-20k' },
      { min: 20000, max: 30000, label: '20k-30k' },
      { min: 30000, max: 40000, label: '30k-40k' },
      { min: 40000, max: 50000, label: '40k-50k' },
      { min: 50000, max: Infinity, label: '50k+' }
    ];

    const distribution = priceRanges.map(range => {
      return inventory.filter(car => 
        parseFloat(car.purchase_price) >= range.min && parseFloat(car.purchase_price) < range.max
      ).length;
    });

    return {
      labels: priceRanges.map(range => range.label),
      datasets: [{
        label: 'Number of Vehicles',
        data: distribution,
        backgroundColor: '#2196f3',
        borderColor: '#1976d2',
        borderWidth: 1,
      }]
    };
  };

  const getProfitDistributionData = (inventory, reports, expenses) => {
    // Get last 6 months
    const today = new Date();
    const months = [];
    for (let i = 0; i < 6; i++) {
      const month = new Date(today.getFullYear(), today.getMonth() - i, 1);
      months.push({
        month: month.toLocaleString('default', { month: 'long' }),
        year: month.getFullYear(),
        monthNum: month.getMonth()
      });
    }

    // Calculate profits for each month
    const monthlyProfits = months.map(monthData => {
      // Calculate vehicle profits
      const monthProfit = inventory
        .filter(car => {
          if (!car.date_sold || car.sale_status !== 'sold') return false;
          const saleDate = new Date(car.date_sold);
          return saleDate.getMonth() === monthData.monthNum && 
                 saleDate.getFullYear() === monthData.year;
        })
        .reduce((total, car) => {
          const carReconditioning = reports
            .filter(report => report.vin === car.vin)
            .reduce((sum, report) => sum + parseFloat(report.cost || 0), 0);
          
          const salePrice = parseFloat(car.sale_price || 0);
          const purchasePrice = parseFloat(car.purchase_price || 0);
          const profit = salePrice - purchasePrice - carReconditioning;
          return total + profit;
        }, 0);

      // Calculate monthly expenses
      const monthlyExpenses = expenses
        ? expenses
            .filter(expense => {
              const expenseDate = new Date(expense.date);
              return expenseDate.getMonth() === monthData.monthNum && 
                     expenseDate.getFullYear() === monthData.year;
            })
            .reduce((total, expense) => total + parseFloat(expense.amount || 0), 0)
        : 0;

      return {
        label: `${monthData.month} ${monthData.year}`,
        profit: Math.round(monthProfit - monthlyExpenses) // Subtract expenses from profit
      };
    });

    return {
      labels: monthlyProfits.map(m => m.label),
      datasets: [{
        label: 'Net Profit',
        data: monthlyProfits.map(m => m.profit),
        backgroundColor: monthlyProfits.map(m => 
          m.profit >= 0 
            ? 'rgba(76, 175, 80, 0.75)' 
            : 'rgba(244, 67, 54, 0.75)'
        ),
        borderColor: monthlyProfits.map(m => 
          m.profit >= 0 
            ? 'rgba(56, 142, 60, 1)' 
            : 'rgba(211, 47, 47, 1)'
        ),
        borderWidth: 1,
        borderRadius: 6,
        maxBarThickness: 50,
        minBarLength: 6
      }]
    };
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  const basePlugins = {
    legend: {
      display: false
    },
    tooltip: {
      backgroundColor: 'rgba(17, 24, 39, 0.95)',
      titleFont: {
        size: 13,
        weight: 'bold',
        family: "'Inter', sans-serif"
      },
      bodyFont: {
        size: 12,
        family: "'Inter', sans-serif"
      },
      padding: 12,
      cornerRadius: 8,
      displayColors: false,
      callbacks: {
        title: function(context) {
          return context[0].label;
        },
        label: function(context) {
          const value = context.raw;
          // Check which dataset we're looking at based on the label
          const label = context.dataset.label === 'Reconditioning Cost' ? 
            'Reconditioning Cost' : 'Gross Profit';
          return `${label}: ${formatCurrency(value)}`;
        }
      }
    },
    datalabels: {
      anchor: 'center',
      align: 'center',
      color: '#000',
      font: {
        weight: 'bold'
      },
      formatter: function(value) {
        return formatCurrency(value);
      }
    }
  };

  const barOptions = {
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: 'y',
    plugins: basePlugins,
    scales: {
      x: {
        beginAtZero: true,
        border: {
          display: false
        },
        grid: {
          color: context => context.tick.value === 0 ? 'rgba(0,0,0,0.2)' : 'rgba(0,0,0,0.05)',
          drawBorder: false
        },
        ticks: {
          maxRotation: 0,
          padding: 8,
          font: {
            size: 11,
            weight: 'bold',
            family: "'Inter', sans-serif"
          },
          callback: function(value) {
            return new Intl.NumberFormat('en-US', {
              style: 'currency',
              currency: 'USD',
              minimumFractionDigits: 0,
              maximumFractionDigits: 0
            }).format(value);
          },
          stepSize: 20000
        }
      },
      y: {
        border: {
          display: false
        },
        grid: {
          display: false
        },
        ticks: {
          padding: 8,
          font: {
            size: 11,
            weight: 'bold',
            family: "'Inter', sans-serif"
          }
        }
      }
    },
    animation: {
      duration: 1000,
      easing: 'easeInOutQuart'
    }
  };

  const handleReconditioningClick = () => {
    console.log('Reconditioning Click - Data:', {
      cost: dashboardData?.current_month_reconditioning_cost,
      month: dashboardData?.current_month_name,
      reports: dashboardData?.reconditioning_reports?.length || 0
    });
    
    navigate('/monthly-reconditioning', { 
      state: { 
        month: dashboardData?.current_month_name,
        year: new Date().getFullYear(),
        reconditioning_reports: dashboardData?.reconditioning_reports || []
      }
    });
  };

  if (loading)
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress />
      </Box>
    );

  if (error)
    return (
      <Typography color="error" align="center" mt={5}>
        {error}
      </Typography>
    );

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      <Box sx={{ 
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column'
      }}>
        <Container maxWidth="lg" sx={{ px: { xs: 0.5, sm: 2 }, mb: { xs: 2, sm: 3 }, flex: 1 }}>
          <Box mt={3} textAlign="center">
            <motion.div variants={itemVariants}>
              <Typography
                variant="h4"
                gutterBottom
                sx={{
                  fontWeight: 800,
                  fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem' },
                  color: 'text.primary',
                  mb: { xs: 2, sm: 3 }
                }}
              >
                Welcome to Your Dashboard
              </Typography>
            </motion.div>

            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Grid container spacing={2}>
                  {[
                    {
                      title: 'Active Inventory',
                      value: `${dashboardData?.total_vehicles || 0} Vehicles`,
                      icon: <InventoryIcon sx={{ fontSize: 40, opacity: 0.7 }} />,
                      onClick: handleViewInventory
                    },
                    {
                      title: 'Active Inventory Value',
                      value: formatCurrency(dashboardData?.total_inventory_value),
                      icon: <TrendingUpIcon sx={{ fontSize: 40, opacity: 0.7 }} />,
                      onClick: handleViewInventory
                    },
                    {
                      title: 'Monthly Reconditioning',
                      value: formatCurrency(dashboardData?.current_month_reconditioning_cost || 0),
                      subtitle: `(${dashboardData?.current_month_name || ''})`,
                      icon: <BuildIcon sx={{ fontSize: 40, opacity: 0.7 }} />,
                      onClick: handleReconditioningClick
                    },
                    {
                      title: 'Monthly Profit',
                      subtitle: `(${dashboardData?.current_month_name || ''})`,
                      value: formatCurrency(dashboardData?.current_month_profit),
                      icon: <TrendingUpIcon sx={{ fontSize: 40, opacity: 0.7 }} />,
                    },
                    {
                      title: 'Vehicles on Floor Plan',
                      value: dashboardData?.total_floor_plan || 0,
                      icon: <DirectionsCarIcon sx={{ fontSize: 40, opacity: 0.7 }} />,
                      onClick: () => navigate('/inventory?sale_type=floor')
                    },
                    {
                      title: 'Dealer Owned Vehicles',
                      value: dashboardData?.total_dealership || 0,
                      icon: <StorefrontIcon sx={{ fontSize: 40, opacity: 0.7 }} />,
                      onClick: () => navigate('/inventory?sale_type=dealer')
                    },
                    {
                      title: 'Consignment Vehicles',
                      value: dashboardData?.total_consignment || 0,
                      icon: <HandshakeIcon sx={{ fontSize: 40, opacity: 0.7 }} />,
                      onClick: () => navigate('/inventory?sale_type=consignment')
                    },
                    {
                      title: 'Reconditioning for Unsold Inventory',
                      value: formatCurrency(dashboardData?.unsold_reconditioning_cost),
                      icon: <BuildIcon sx={{ fontSize: 40, opacity: 0.7 }} />,
                      onClick: () => navigate('/unsold-reconditioning')
                    },
                    {
                      title: 'Admin Costs',
                      value: formatCurrency(dashboardData?.total_expenses || 0),
                      icon: <AccountBalanceWalletIcon sx={{ fontSize: 40, opacity: 0.7 }} />,
                      onClick: () => navigate('/expenses')
                    },
                    {
                      title: 'Monthly Deposits',
                      subtitle: `(${dashboardData?.current_month_name || ''})`,
                      value: formatCurrency(dashboardData?.current_month_deposits || 0),
                      icon: <AccountBalanceWalletIcon sx={{ fontSize: 40, opacity: 0.7 }} />,
                      onClick: () => navigate('/deposits')
                    },
                    {
                      title: 'Incomplete Tasks',
                      value: dashboardData?.incomplete_tasks || 0,
                      icon: <AssignmentLateIcon sx={{ fontSize: 40, opacity: 0.7 }} />,
                      onClick: () => navigate('/tasks')
                    },
                    {
                      title: 'Inventory Analytics',
                      value: (
                        <Box display="flex" flexDirection="column" alignItems="center" sx={{ width: '100%' }}>
                          <Box 
                            display="flex" 
                            alignItems="center" 
                            justifyContent="space-between" 
                            sx={{ 
                              width: 'calc(100% - 16px)',
                              p: 1
                            }}
                          >
                            <Box textAlign="center" flex={1}>
                              <Typography
                                variant="h6"
                                sx={{
                                  fontWeight: 800,
                                  color: 'text.primary',
                                  fontSize: { xs: '1.1rem', sm: '1.3rem', md: '1.5rem' },
                                  lineHeight: 1
                                }}
                              >
                                {calculateAverageDaysToSale(dashboardData?.inventory).replace(' Days', '')}
                              </Typography>
                              <Typography
                                variant="caption"
                                sx={{
                                  color: 'text.secondary',
                                  fontWeight: 600,
                                  fontSize: { xs: '0.65rem', sm: '0.7rem', md: '0.75rem' },
                                  textTransform: 'uppercase',
                                  letterSpacing: 0.5
                                }}
                              >
                                Avg. Sale Days
                              </Typography>
                            </Box>
                            
                            <Divider 
                              orientation="vertical" 
                              flexItem 
                              sx={{ 
                                mx: 1, 
                                borderColor: 'rgba(0, 0, 0, 0.2)',
                                borderWidth: 1
                              }} 
                            />
                            
                            <Box textAlign="center" flex={1}>
                              <Typography
                                variant="h6"
                                sx={{
                                  fontWeight: 800,
                                  color: 'text.primary',
                                  fontSize: { xs: '1.1rem', sm: '1.3rem', md: '1.5rem' },
                                  lineHeight: 1
                                }}
                              >
                                {calculateAverageAgeOfCurrentInventory(dashboardData?.inventory).replace(' Days', '')}
                              </Typography>
                              <Typography
                                variant="caption"
                                sx={{
                                  color: 'text.secondary',
                                  fontWeight: 600,
                                  fontSize: { xs: '0.65rem', sm: '0.7rem', md: '0.75rem' },
                                  textTransform: 'uppercase',
                                  letterSpacing: 0.5
                                }}
                              >
                                Current Age
                              </Typography>
                            </Box>
                          </Box>
                        </Box>
                      ),
                      icon: <AccessTimeIcon sx={{ fontSize: 40, opacity: 0.7 }} />
                    }
                  ].map((item, index) => (
                    <Grid item xs={6} sm={6} md={3} key={index}>
                      <motion.div variants={itemVariants}>
                        <Paper
                          elevation={0}
                          onClick={item.onClick}
                          sx={{
                            padding: { xs: 1, sm: 1.5, md: 2 },
                            borderRadius: 2,
                            background: theme.palette.background.paper,
                            border: `1px solid ${theme.palette.divider}`,
                            transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                            cursor: item.onClick ? 'pointer' : 'default',
                            '&:hover': {
                              transform: 'translateY(-3px)',
                              boxShadow: theme.shadows[4]
                            },
                            minHeight: { xs: '90px', sm: '120px' },
                            height: { xs: '120px', sm: '140px', md: '150px' },
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center'
                          }}
                        >
                          <Box display="flex" alignItems="center" mb={0.5}>
                            <Box sx={{ color: 'text.secondary' }}>
                              {React.cloneElement(item.icon, { sx: { fontSize: { xs: 20, sm: 24, md: 30 } } })}
                            </Box>
                          </Box>
                          <Typography
                            variant="subtitle2"
                            color="text.secondary"
                            sx={{
                              fontSize: { xs: '0.65rem', sm: '0.75rem', md: '0.9rem' },
                              mb: 0.5,
                              lineHeight: 1.2
                            }}
                          >
                            {item.title}
                            {item.subtitle && (
                              <Typography
                                component="span"
                                sx={{
                                  fontSize: { xs: '0.55rem', sm: '0.65rem', md: '0.8rem' },
                                  ml: 0.5,
                                  opacity: 0.8
                                }}
                              >
                                {item.subtitle}
                              </Typography>
                            )}
                          </Typography>
                          <Typography
                            variant="h6"
                            sx={{
                              fontWeight: 700,
                              color: 'text.primary',
                              fontSize: { xs: '0.85rem', sm: '1rem', md: '1.4rem' },
                              lineHeight: 1.2
                            }}
                          >
                            {item.value}
                          </Typography>
                        </Paper>
                      </motion.div>
                    </Grid>
                  ))}
                </Grid>
              </Grid>

              <Grid item xs={12}>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Paper
                      elevation={0}
                      sx={{
                        padding: { xs: 2, sm: 3 },
                        borderRadius: 3,
                        height: '100%',
                        background: theme.palette.mode === 'dark' 
                          ? 'linear-gradient(180deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.02) 100%)'
                          : 'linear-gradient(180deg, rgba(255,255,255,1) 0%, rgba(245,245,245,1) 100%)',
                        boxShadow: theme.palette.mode === 'dark'
                          ? '0 4px 20px 0 rgba(0,0,0,0.2)'
                          : '0 4px 20px 0 rgba(0,0,0,0.05)',
                        border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)'}`,
                      }}
                    >
                      <Typography variant="h6" gutterBottom>
                        Last 6 Months Reconditioning
                      </Typography>
                      <Box sx={{ height: 220 }}>
                        <Bar 
                          data={getReconditioningData(dashboardData?.reconditioning_reports || [])} 
                          options={barOptions} 
                        />
                      </Box>
                    </Paper>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Paper
                      elevation={0}
                      sx={{
                        padding: { xs: 2, sm: 3 },
                        borderRadius: 3,
                        height: '100%',
                        background: theme.palette.mode === 'dark' 
                          ? 'linear-gradient(180deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.02) 100%)'
                          : 'linear-gradient(180deg, rgba(255,255,255,1) 0%, rgba(245,245,245,1) 100%)',
                        boxShadow: theme.palette.mode === 'dark'
                          ? '0 4px 20px 0 rgba(0,0,0,0.2)'
                          : '0 4px 20px 0 rgba(0,0,0,0.05)',
                        border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)'}`,
                      }}
                    >
                      <Typography variant="h6" gutterBottom>
                        Last 6 Months Gross Profit
                      </Typography>
                      <Box sx={{ height: 220, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Bar
                          data={getProfitDistributionData(
                            dashboardData?.inventory || [],
                            dashboardData?.reconditioning_reports || [],
                            dashboardData?.expenses || []
                          )}
                          options={barOptions}
                        />
                      </Box>
                    </Paper>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Box>
        </Container>

        <Box
          component="footer"
          sx={{
            py: 3,
            px: 2,
            mt: 'auto',
            backgroundColor: theme.palette.mode === 'dark' 
              ? 'rgba(255,255,255,0.05)'
              : 'rgba(0,0,0,0.03)',
            borderTop: `1px solid ${theme.palette.mode === 'dark' 
              ? 'rgba(255,255,255,0.1)' 
              : 'rgba(0,0,0,0.1)'}`,
            position: 'relative',
            left: isMobile ? 0 : '-80px',
            width: isMobile ? '100%' : 'calc(100% + 80px)',
            textAlign: 'center'
          }}
        >
          <Container maxWidth="lg">
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 2
              }}
            >
              <Typography
                variant="body2"
                color="text.secondary"
                align="center"
              >
                {'© '}
                {new Date().getFullYear()}
                {' DealCost. All rights reserved.'}
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  gap: 3,
                  justifyContent: 'center',
                  alignItems: 'center'
                }}
              >
                <Link
                  href="#"
                  color="text.secondary"
                  sx={{ 
                    textDecoration: 'none',
                    '&:hover': { color: 'primary.main' }
                  }}
                >
                  Privacy Policy
                </Link>
                <Link
                  href="#"
                  color="text.secondary"
                  sx={{ 
                    textDecoration: 'none',
                    '&:hover': { color: 'primary.main' }
                  }}
                >
                  Terms of Service
                </Link>
                <Link
                  href="#"
                  color="text.secondary"
                  sx={{ 
                    textDecoration: 'none',
                    '&:hover': { color: 'primary.main' }
                  }}
                >
                  Contact
                </Link>
              </Box>
            </Box>
          </Container>
        </Box>
      </Box>
    </motion.div>
  );
}

export default Home;